import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import AmbiguityModal from '../components/AmbiguityModal';
import VersionHistory from '../components/VersionHistory';
import RichTextEditor from '../components/RichTextEditor';
import MergeReport from '../components/MergeReport';
import ComplianceReport from '../components/ComplianceReport';
import ExportMenu from '../components/ExportMenu';
import RefinementPanel from '../components/RefinementPanel';
import PublishWorkflow from '../components/PublishWorkflow';
import MergeCoverageBar from '../components/MergeCoverageBar';
import SourceAttribution from '../components/SourceAttribution';

const REPORT_POLL_INTERVAL_MS = 4000;
const REPORT_POLL_TIMEOUT_MS = 120000;
const ALLOWED_VERSION_TABS = new Set(['policy', 'report', 'compliance']);

const getTabFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');
  if (tab && ALLOWED_VERSION_TABS.has(tab)) {
    return tab;
  }
  return 'policy';
};

const VersionPage = () => {
  const { projectId, versionId } = useParams();
  const location = useLocation();
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState('');
  const [reversing, setReversing] = useState(false);
  const [ambiguities, setAmbiguities] = useState([]);
  const [mergeReport, setMergeReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [reportPollTimedOut, setReportPollTimedOut] = useState(false);
  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));
  const [complianceReport, setComplianceReport] = useState(null);
  const reportPollStartedAtRef = useRef(null);
  const navigate = useNavigate();
  const reportStatus = version?.merge_report_status || 'not_requested';
  const reportError = version?.merge_report_error;

  const fetchMergeReport = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setReportLoading(true);
    }
    try {
      const { data } = await api.get(`/api/versions/${versionId}/report`);
      setMergeReport(data);
    } catch (error) {
      setMergeReport(null);
    } finally {
      if (!silent) {
        setReportLoading(false);
      }
    }
  }, [versionId]);

  const fetchVersion = useCallback(async (options = {}) => {
    const { silent = false, includeAmbiguities = true } = options;
    if (!silent) {
      setLoading(true);
    }
    try {
      const { data } = await api.get(`/api/versions/${versionId}`);
      setVersion(data);

      if (includeAmbiguities) {
        const ambRes = await api.get(`/api/ambiguity/version/${versionId}`);
        setAmbiguities(ambRes.data.filter((a) => !a.resolved_at));
      }
    } catch (error) {
      if (!silent) {
        toast.error('Failed to load policy version');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [versionId]);

  const fetchComplianceReport = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setComplianceLoading(true);
    }
    try {
      const { data } = await api.get(`/api/versions/${versionId}/compliance-report`);
      setComplianceReport(data);
    } catch {
      setComplianceReport(null);
    } finally {
      if (!silent) {
        setComplianceLoading(false);
      }
    }
  }, [versionId]);

  useEffect(() => {
    setActiveTab(getTabFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    fetchVersion();
    fetchMergeReport();
    fetchComplianceReport();
  }, [fetchVersion, fetchMergeReport, fetchComplianceReport]);

  useEffect(() => {
    if (!['queued', 'processing'].includes(reportStatus)) {
      reportPollStartedAtRef.current = null;
      if (reportPollTimedOut) {
        setReportPollTimedOut(false);
      }
      return undefined;
    }
    if (!versionId) {
      return undefined;
    }
    if (activeTab !== 'report' || reportPollTimedOut) {
      return undefined;
    }
    if (!reportPollStartedAtRef.current) {
      reportPollStartedAtRef.current = Date.now();
    }

    const pollId = setInterval(() => {
      const startedAt = reportPollStartedAtRef.current || Date.now();
      if (Date.now() - startedAt >= REPORT_POLL_TIMEOUT_MS) {
        clearInterval(pollId);
        setReportPollTimedOut(true);
        toast.error('Report polling timed out. Retry generation if status stays in progress.');
        return;
      }
      fetchVersion({ silent: true, includeAmbiguities: false });
    }, REPORT_POLL_INTERVAL_MS);

    return () => clearInterval(pollId);
  }, [versionId, reportStatus, activeTab, reportPollTimedOut, fetchVersion]);

  useEffect(() => {
    if (reportStatus === 'ready' && !mergeReport) {
      fetchMergeReport({ silent: true });
    }
  }, [reportStatus, mergeReport, fetchMergeReport]);

  const handleRevise = async () => {
    if (!suggestions.trim()) {
      toast.error('Please enter revision suggestions');
      return;
    }

    setReversing(true);
    try {
      toast.loading('Revising policy...', { id: 'revise' });
      const { data } = await api.post(`/api/versions/${versionId}/revise`, { suggestions });
      toast.success('Revision complete!', { id: 'revise' });
      navigate(`/projects/${projectId}/versions/${data.version_id}`);
      setSuggestions('');
    } catch (error) {
      toast.error('Revision failed', { id: 'revise' });
    } finally {
      setReversing(false);
    }
  };

  const handleGenerateReport = async (force = false) => {
    setReportPollTimedOut(false);
    reportPollStartedAtRef.current = null;
    setReportLoading(true);
    try {
      const { data } = await api.post(`/api/versions/${versionId}/generate-report${force ? '?force=true' : ''}`);
      if (data?.id) {
        setMergeReport(data);
        toast.success('Merge report generated');
      } else {
        toast.success(force ? 'Merge report regeneration started' : 'Merge report generation started');
      }
      setActiveTab('report');
      fetchVersion();
      fetchMergeReport();
    } catch (error) {
      toast.error('Failed to generate merge report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleGenerateComplianceReport = async (force = false) => {
    setComplianceLoading(true);
    try {
      const { data } = await api.post(`/api/versions/${versionId}/generate-compliance-report${force ? '?force=true' : ''}`);
      setComplianceReport(data);
      setActiveTab('compliance');
      toast.success(force ? 'Compliance report regenerated' : 'Compliance report generated');
    } catch {
      toast.error(force ? 'Failed to regenerate compliance report' : 'Failed to generate compliance report');
    } finally {
      setComplianceLoading(false);
    }
  };

  const handleResumeReportPolling = () => {
    setReportPollTimedOut(false);
    reportPollStartedAtRef.current = null;
    fetchVersion({ silent: true, includeAmbiguities: false });
  };

  const handleAmbiguityResolved = () => {
    setAmbiguities([]);
    toast.success('All issues resolved. You can now revise the policy.');
  };

  const handleRestore = (newVersion) => {
    navigate(`/projects/${projectId}/versions/${newVersion.id}`);
  };

  if (loading) return <div className="loading">Loading merged policy...</div>;
  if (!version) return <div>Version not found.</div>;

  return (
    <div className="version-page">
      <div className="version-page__header">
        <div className="title-area">
          <h1>Merged Policy - Version {version.version_number}</h1>
          <div className="version-meta">
            <span>Created: {new Date(version.created_at).toLocaleString()}</span>
          </div>
        </div>
        <div className="header-actions">
          <PublishWorkflow
            projectId={projectId}
            versionId={versionId}
            onPublished={() => toast.success('Policy added to publish workflow')}
          />
          <ExportMenu versionId={versionId} reportId={mergeReport?.id} />
        </div>
      </div>

      {ambiguities.length > 0 && (
        <AmbiguityModal ambiguities={ambiguities} onResolved={handleAmbiguityResolved} />
      )}

      <div className="project-tabs">
        <button type="button" className={`tab-btn ${activeTab === 'policy' ? 'active' : ''}`} onClick={() => setActiveTab('policy')}>
          Policy
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`}
          onClick={() => setActiveTab('report')}
          disabled={!mergeReport && reportLoading}
        >
          Merge Report
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'compliance' ? 'active' : ''}`}
          onClick={() => setActiveTab('compliance')}
          disabled={complianceLoading}
        >
          Compliance Report
        </button>
      </div>

      {activeTab === 'policy' && (
        <div className="version-layout">
          <div className="version-main">
            <MergeCoverageBar mergeQuality={version.merge_quality} />
            <SourceAttribution sectionAttribution={version.section_attribution} />

            <div className="version-content">
              <RichTextEditor value={version.content} onChange={(val) => setVersion({ ...version, content: val })} />
            </div>

            <div className="revision-panel">
              <h3>Refine Policy</h3>
              <p>Give instructions to the AI to adjust the tone, add sections, or clarify language.</p>
              <textarea
                value={suggestions}
                onChange={(e) => setSuggestions(e.target.value)}
                placeholder="e.g., Make the tone more patient-friendly..."
              />
              <button type="button" className="btn btn--primary" onClick={handleRevise} disabled={reversing || ambiguities.length > 0}>
                {reversing ? 'Revising...' : 'Apply Revision'}
              </button>
              {ambiguities.length > 0 && <p className="alert">Resolve issues above before revising.</p>}
            </div>

            <RefinementPanel
              versionId={versionId}
              onMerged={(payload) => navigate(`/projects/${projectId}/versions/${payload.version_id}`)}
            />
          </div>

          <aside className="version-sidebar">
            <VersionHistory projectId={projectId} currentVersionId={versionId} onRestore={handleRestore} />
          </aside>
        </div>
      )}

      {activeTab === 'report' && (
        <div className="panel panel--spaced">
          {reportLoading && <div className="loading">Loading report...</div>}
          {!reportLoading && ['queued', 'processing'].includes(reportStatus) && (
            <div className="empty-state empty-state--stacked">
              <p>Merge report is {reportStatus === 'queued' ? 'queued' : 'in progress'}.</p>
              {!reportPollTimedOut && <p className="muted">This page refreshes automatically while report generation runs.</p>}
              {reportPollTimedOut && (
                <>
                  <p className="muted">Auto-refresh paused after 2 minutes to avoid endless polling.</p>
                  <button type="button" className="btn btn--primary" onClick={handleResumeReportPolling}>Resume Auto-refresh</button>
                </>
              )}
            </div>
          )}
          {!reportLoading && reportStatus === 'failed' && !mergeReport && (
            <div className="empty-state empty-state--stacked">
              <p>{reportError || 'Merge report generation failed.'}</p>
              <button type="button" className="btn btn--primary" onClick={handleGenerateReport}>Retry Merge Report</button>
            </div>
          )}
          {!reportLoading && !mergeReport && !['queued', 'processing', 'failed'].includes(reportStatus) && (
            <div className="empty-state empty-state--stacked">
              <p>No merge report exists for this version.</p>
              <button type="button" className="btn btn--primary" onClick={handleGenerateReport}>Generate Merge Report</button>
            </div>
          )}
          {!reportLoading && mergeReport && (
            <>
              <div className="row-actions">
                <p className="muted">Report created {mergeReport.created_at ? new Date(mergeReport.created_at).toLocaleString() : 'recently'}.</p>
                <button type="button" className="btn btn--sm" onClick={() => handleGenerateReport(true)}>Regenerate Merge Report</button>
              </div>
              <MergeReport report={mergeReport} />
            </>
          )}
        </div>
      )}

      {activeTab === 'compliance' && (
        <div className="panel panel--spaced">
          {complianceLoading && <div className="loading">Loading compliance report...</div>}
          {!complianceLoading && !complianceReport && (
            <div className="empty-state empty-state--stacked">
              <p>No compliance report exists for this version.</p>
              <button type="button" className="btn btn--primary" onClick={() => handleGenerateComplianceReport(false)}>
                Generate Compliance Report
              </button>
            </div>
          )}
          {!complianceLoading && complianceReport && (
            <>
              <div className="row-actions">
                <p className="muted">Report created {complianceReport.created_at ? new Date(complianceReport.created_at).toLocaleString() : 'recently'}.</p>
                <button type="button" className="btn btn--sm" onClick={() => handleGenerateComplianceReport(true)}>Regenerate Compliance Report</button>
              </div>
              <ComplianceReport report={complianceReport} />
            </>
          )}
        </div>
      )}

      <div className="version-actions">
        <button type="button" className="btn" onClick={() => navigate(`/projects/${projectId}`)}>
          Back to Project
        </button>
      </div>
    </div>
  );
};

export default VersionPage;
